import { useContext } from 'react';
import Alert from '@mui/material/Alert';
import { WorkingYearNoticeShown } from './workingYearNoticeShown';
import { isPastAcademicYear, useCurrentAcademicYear } from '@/hooks/useCurrentAcademicYear';
import { useAcademicYearWritable } from '@/hooks/useAcademicYearWritable';

export interface YearScopeNoticeProps {
  /** The year the page shows; defaults to the working year. */
  yearId?: number | null;
  /** The shown year's name, when it is not among the school's years (e.g. another school's). */
  yearName?: string;
}

/** The notice text for a shown year, or null when the page shows the open working year. */
function yearScopeNoticeText(
  shownYearName: string,
  { isWorkingYear, isPast, caption }: { isWorkingYear: boolean; isPast: boolean; caption: string | null },
): string | null {
  if (!isWorkingYear) return `Viewing ${shownYearName} — not the working year`;
  if (isPast && caption) return `${shownYearName}: ${caption}`;
  if (isPast) return `Viewing ${shownYearName} — a past academic year`;
  return null;
}

/**
 * Tells the user a page shows a year other than the working year, or that the working
 * year is a past (closed or ended) one chosen in the year switcher. Renders nothing when
 * the page shows the open working year, or when there is no year yet. An administrator
 * who may edit the past working year is told so (a warning: their changes are saved).
 */
export function YearScopeNotice({ yearId, yearName }: YearScopeNoticeProps) {
  const { years, currentYear } = useCurrentAcademicYear();
  const workingNoticeShown = useContext(WorkingYearNoticeShown);
  const { caption } = useAcademicYearWritable();
  const shownId = yearId ?? currentYear?.yearId ?? null;
  if (shownId === null) return null;
  if (workingNoticeShown && shownId === currentYear?.yearId) return null;
  const shown = years.find((y) => y.yearId === shownId) ?? null;
  const name = shown?.yearName ?? yearName;
  if (!name) return null;
  const isWorkingYear = shownId === currentYear?.yearId;
  const isPast = shown !== null && isPastAcademicYear(shown);
  const editing = isWorkingYear && isPast && caption !== null;
  const text = yearScopeNoticeText(name, { isWorkingYear, isPast, caption: editing ? caption : null });
  if (!text) return null;
  return (
    <Alert severity={editing ? 'warning' : 'info'} role="status" sx={{ mb: 2 }}>
      {text}
    </Alert>
  );
}
